import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../rootReducer';

export const ChatContext = {
  LITERATURE_SEARCH: 'literatureSearch',
  RELEVANCE_CHECK: 'relevanceCheck',
};

export interface MethodOutput {
  [key: string]: any;
}

export interface Message {
  sender: 'ai' | 'user' | 'system';
  text: string;
  timestamp?: string;
  methodOutput?: string;
}

export interface Result {
  module: string;
  resultStatus: string;
}

interface ChatState {
  isOpen: boolean;
  messages: Record<string, Message[]>;
  isConnected: boolean;
  isLoading: boolean;
  isInputDisabled: boolean;
  currentContext: string | null; // To track current chat context (e.g., "literatureSearch", "relevanceCheck")
  contextData: any; // To store context-specific data
  result: Result | null;
  chattingInProgress: boolean;
  reInit: boolean;
}

const initialState: ChatState = {
  isOpen: false,
  messages: {},
  isConnected: false,
  isLoading: false,
  isInputDisabled: false,
  currentContext: null,
  contextData: null,
  result: { module: '', resultStatus: '' },
  chattingInProgress: false,
  reInit: false
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setIsOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    addMessage: (state, action: PayloadAction<{ module: string; message: Message }>) => {
      const { module, message } = action.payload;
      if (!state.messages[module]) {
        state.messages[module] = [];
      }
      state.messages[module].push(message);
    },
    clearMessages: (state, action: PayloadAction<{ module: string }>) => {
      const { module } = action.payload;
      if (state.messages[module]) {
        state.messages[module] = [];
      }
    },
    setIsConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsInputDisabled: (state, action: PayloadAction<boolean>) => {
      state.isInputDisabled = action.payload;
    },
    setCurrentContext: (state, action: PayloadAction<string | null>) => {
      state.currentContext = action.payload;
    },
    setContextData: (state, action: PayloadAction<any>) => {
      state.contextData = action.payload;
    },
    setResult: (state, action: PayloadAction<Result>) => {
      state.result = action.payload;
    },
    setChattingInProgress: (state, action: PayloadAction<boolean>) => {
      state.chattingInProgress = action.payload;
    },
    setReInit: (state, action: PayloadAction<boolean>) => {
      state.reInit = action.payload;
    }
  }
});

export const {
  setIsOpen,
  addMessage,
  clearMessages,
  setIsConnected,
  setIsLoading,
  setIsInputDisabled,
  setCurrentContext,
  setContextData,
  setResult,
  setChattingInProgress,
  setReInit
} = chatSlice.actions;

export const chatSelector = (state: RootState) => state.chat;
export const moduleMessagesSelector = (module: string) => (state: RootState) => state.chat.messages[module] || [];

export default chatSlice.reducer;